import * as React from 'react';
import { cva } from 'class-variance-authority';
import type { VariantProps } from 'class-variance-authority';
import { Slot } from 'radix-ui';

import { cn } from '../lib/utils';

const buttonVariants = cva(
  'focus-visible:ring-ring inline-flex cursor-pointer items-center justify-center rounded-md text-sm font-medium whitespace-nowrap transition-colors focus-visible:ring-1 focus-visible:outline-hidden disabled:pointer-events-none disabled:cursor-default disabled:opacity-50',
  {
    variants: {
      variant: {
        default:
          'bg-primary text-primary-foreground hover:bg-primary/90 shadow-xs',
        destructive:
          'bg-destructive text-destructive-foreground hover:bg-destructive/90 shadow-xs',
        // Borderless secondary action (2026-07-05 design system): a soft
        // neutral fill instead of the shadcn hairline outline, so primary
        // (coral fill) and secondary read as two weights of one family.
        // Keeps the `outline` name — it's referenced app-wide.
        outline: 'bg-muted text-foreground hover:bg-muted/70',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-secondary/80 shadow-xs',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'decoration-primary underline-offset-4 hover:underline',
        // Editorial / portfolio register — hairline border, mono
        // uppercase label, flat (no shadow), invert on hover. Built for
        // the public dancer profile so its CTAs read like a print
        // portfolio rather than a SaaS dashboard. Radius follows the
        // base `rounded-md` token, so it renders at the unified 6px
        // inside the public [data-editorial] scope (2026-07-05 system).
        editorial:
          'border-foreground/25 text-foreground hover:bg-foreground hover:text-background hover:border-foreground border bg-transparent font-mono text-[11px] font-normal tracking-[0.18em] uppercase',
      },
      size: {
        default: 'h-9 px-4 py-2',
        sm: 'h-8 rounded-md px-3 text-xs',
        lg: 'h-10 rounded-md px-8',
        icon: 'h-9 w-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends
    React.ComponentPropsWithRef<'button'>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button: React.FC<ButtonProps> = ({
  className,
  variant,
  size,
  asChild = false,
  ...props
}) => {
  const Comp = asChild ? Slot.Root : 'button';

  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
};

Button.displayName = 'Button';

export { Button, buttonVariants };
